// Keep selected variant images steady during idle time. Arqum 2026-10-22.
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.motionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoAdvanceDelay = 5000;
        this.manualPauseUntil = 0;
        this.autoAdvanceTimer = null;
        this.transitionCleanupTimer = null;
        this.isTransitioning = false;
        this.pendingMediaRequest = null;
        this.mediaRequestId = 0;
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.addEventListener(
          'pointerdown',
          () => {
            this.manualPauseUntil = Date.now() + this.autoAdvanceDelay;
            this.startAutoAdvance();
          },
          { passive: true }
        );
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.elements.viewer.querySelectorAll('img').forEach((image) => {
          image.loading = 'eager';
          image.decode?.().catch(() => {});
        });
        this.startAutoAdvance();
      }

      disconnectedCallback() {
        window.clearInterval(this.autoAdvanceTimer);
        this.mediaRequestId += 1;
        this.pendingMediaRequest = null;
        this.clearMediaTransition();
        window.clearTimeout(this.announcementTimer);
      }

      onSlideChanged(event) {
        // Ignore empty slide events during resize and variant updates. Arqum 2026-09-14.
        const mediaId = event?.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId || !this.isConnected || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      async setActiveMedia(mediaId, prepend, direction) {
        if (!this.elements.viewer) return;
        // Finish the current slide before applying the latest rapid click.
        if (this.isTransitioning && !prepend) {
          this.pendingMediaRequest = [mediaId, prepend, direction];
          return;
        }
        this.pendingMediaRequest = null;
        const requestId = ++this.mediaRequestId;
        if (prepend) this.clearMediaTransition();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Keep the current image painted until the requested image is decoded.
        const image = activeMedia.querySelector('.product__media img');
        if (image) {
          image.loading = 'eager';
          try { await image.decode?.(); } catch (_) { /* Retain native fallback if decoding is unavailable. */ }
        }
        if (requestId !== this.mediaRequestId || !this.isConnected || !activeMedia.isConnected) return;

        const previousMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const shouldAnimate =
          !prepend &&
          previousMedia &&
          previousMedia !== activeMedia &&
          this.mql.matches &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          !this.motionMql.matches;

        if (shouldAnimate) {
          this.prepareMediaTransition(previousMedia, activeMedia, direction);
        } else {
          this.clearMediaTransition();
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (shouldAnimate || requestId !== this.mediaRequestId || !this.isConnected) return;

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: this.motionMql.matches ? 'auto' : 'smooth',
            });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      startAutoAdvance() {
        window.clearInterval(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
        if (this.closest('product-info')?.querySelector('variant-selects')) return;

        const mediaTargets = this.elements.thumbnails.querySelectorAll('[data-target]');
        if (mediaTargets.length < 2 || this.motionMql.matches || window.Shopify?.designMode) return;

        this.autoAdvanceTimer = window.setInterval(() => this.advanceMedia(), this.autoAdvanceDelay);
      }

      advanceMedia() {
        // Pointer clicks/taps pause briefly, not indefinitely; keyboard focus still pauses playback.
        if (
          this.closest('product-info')?.querySelector('variant-selects') ||
          this.isTransitioning ||
          this.motionMql.matches ||
          document.hidden ||
          Date.now() < this.manualPauseUntil ||
          this.querySelector(':focus-visible')
        ) {
          return;
        }

        const mediaTargets = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const currentIndex = mediaTargets.findIndex((item) =>
          item.querySelector('button[aria-current="true"]')
        );
        const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % mediaTargets.length;
        const nextTarget = mediaTargets[nextIndex]?.dataset.target;
        if (!nextTarget) return;

        this.setActiveMedia(nextTarget, false, 'forward');
      }

      prepareMediaTransition(previousMedia, activeMedia, direction) {
        this.clearMediaTransition();
        const slider = previousMedia.parentElement;
        const slides = Array.from(slider.children);
        const isForward = direction === 'forward' ||
          (direction !== 'reverse' && slides.indexOf(activeMedia) > slides.indexOf(previousMedia));

        slider.style.setProperty('--ips-gallery-height',
          Math.max(previousMedia.getBoundingClientRect().height, 1) + 'px');
        slider.classList.add('ips-gallery-is-transitioning');
        slider.classList.toggle('ips-gallery-is-reverse', !isForward);
        previousMedia.classList.add('ips-gallery-outgoing');
        activeMedia.classList.add('ips-gallery-incoming');
        this.isTransitioning = true;
        this.transitionMedia = activeMedia;

        this.transitionEndHandler = (event) => {
          if (event && (event.target !== activeMedia || !event.animationName.startsWith('ips-gallery-slide-in'))) return;
          const pending = this.pendingMediaRequest;
          this.pendingMediaRequest = null;
          // Remove both animation layers together, without scrolling the desktop gallery.
          this.clearMediaTransition();
          if (pending) this.setActiveMedia(...pending);
        };
        activeMedia.addEventListener('animationend', this.transitionEndHandler);
        // Fallback for a breakpoint change or a background tab cancelling the animation.
        this.transitionCleanupTimer = window.setTimeout(() => this.transitionEndHandler?.(), 1100);
      }

      clearMediaTransition() {
        window.clearTimeout(this.transitionCleanupTimer);
        this.transitionCleanupTimer = null;
        if (this.transitionMedia && this.transitionEndHandler) {
          this.transitionMedia.removeEventListener('animationend', this.transitionEndHandler);
        }
        this.transitionMedia = null;
        this.transitionEndHandler = null;
        this.isTransitioning = false;

        const slider = this.elements.viewer?.slider ||
          this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!slider) return;
        slider.classList.remove('ips-gallery-is-transitioning', 'ips-gallery-is-reverse');
        slider.style.removeProperty('--ips-gallery-height');
        slider.querySelectorAll('.ips-gallery-outgoing, .ips-gallery-incoming')
          .forEach((item) => item.classList.remove('ips-gallery-outgoing', 'ips-gallery-incoming'));
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        const announce = () => {
          if (!activeItem.classList.contains('is-active') || !this.isConnected) return;
          window.clearTimeout(this.announcementTimer);
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          this.announcementTimer = window.setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        // Never reassign src: doing so can blank/redecode an already visible image.
        if (image.complete && image.naturalWidth) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
